import React from "react"
import { url } from "aldoo-ra/CMS/url"

/**
 * Renders a user avatar with automatic update support
 *
 * This helper ensures all avatars have the data-user-avatar attribute
 * so they can be updated automatically when a user changes their avatar.
 *
 * @param {object} user - User object with avatar, name, email, _id
 * @param {string} sizeClass - Tailwind size classes (e.g., "w-10 h-10")
 * @param {string} textSizeClass - Text size for initials (e.g., "text-sm")
 * @returns {JSX.Element} Avatar image or initials
 */
export function renderAvatar(user, sizeClass = "w-10 h-10", textSizeClass = "text-sm") {
  if (!user) {
    return (
      <div className={`${sizeClass} rounded-full bg-admin_neutral_2 flex items-center justify-center flex-shrink-0`}>
        <span className={`text-admin_text_secondary font-medium ${textSizeClass}`}>?</span>
      </div>
    )
  }

  const userId = user._id || user.id
  const initial = (user.name || user.email || 'U').charAt(0).toUpperCase()

  return (
    <div className={`${sizeClass} rounded-full bg-admin_primary flex items-center justify-center flex-shrink-0 overflow-hidden`}>
      {user.avatar ? (
        <img
          src={url(user.avatar)}
          alt={user.name || user.email}
          className={`${sizeClass} rounded-full object-cover bg-white`}
          data-user-avatar={userId}
        />
      ) : (
        <span className={`text-white font-medium ${textSizeClass}`}>
          {initial}
        </span>
      )}
    </div>
  )
}

/**
 * Inline avatar image component (for use in JSX without wrapper div)
 *
 * Use this when you need just the img element without the wrapper
 *
 * @param {object} user - User object with avatar, name, email, _id
 * @param {string} className - CSS classes for the img element
 * @returns {JSX.Element|null} Avatar image element or null
 */
export function AvatarImg({ user, className = "" }) {
  if (!user?.avatar) return null

  const userId = user._id || user.id

  return (
    <img
      src={url(user.avatar)}
      alt={user.name || user.email}
      className={className}
      data-user-avatar={userId}
    />
  )
}
